import React from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import { logout, selectCurrentUser } from "../../store/user/user";
import styles from "./Sidebar.module.css";

function Sidebar() {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);

  if (!currentUser) return null;

  const { username, elo, stats, history } = currentUser;
  const totalMatches = stats.wins + stats.losses + stats.draws;
  const winRate = totalMatches > 0 ? Math.round((stats.wins / totalMatches) * 100) : 0;

  // Determine Rank Tier based on Elo rating
  const getRankTier = (rating) => {
    if (rating < 1000) return { title: "Pemula", color: "#9ca3af" }; // gray
    if (rating < 1200) return { title: "Perintis", color: "#60a5fa" }; // blue
    if (rating < 1400) return { title: "Ksatria", color: "#34d399" }; // green
    if (rating < 1600) return { title: "Perwira", color: "#fb7185" }; // rose
    if (rating < 1800) return { title: "Master", color: "#a78bfa" }; // purple
    return { title: "Grandmaster", color: "#fbbf24" }; // amber
  };

  const rank = getRankTier(elo);

  const formatDate = (isoString) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString("id-ID", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return "";
    }
  };

  return (
    <aside className={styles.sidebar}>
      {/* Profile Card */}
      <div className={styles.profileCard}>
        <div className={styles.avatar}>
          {username.substring(0, 2).toUpperCase()}
        </div>
        <div className={styles.profileInfo}>
          <h2 className={styles.username}>{username}</h2>
          <div className={styles.rankBadge} style={{ backgroundColor: rank.color + "22", borderColor: rank.color, color: rank.color }}>
            {rank.title}
          </div>
          <p className={styles.eloDisplay}>
            Rating: <span className={styles.eloNumber}>{elo}</span> ELO
          </p>
        </div>
      </div>

      {/* Stats Section */}
      <div className={styles.statsCard}>
        <h3 className={styles.sectionTitle}>Statistik Anda</h3>
        
        <div className={styles.winRateContainer}>
          <div className={styles.winRateHeader}>
            <span>Rasio Kemenangan</span>
            <span className={styles.winRateValue}>{winRate}%</span>
          </div>
          <div className={styles.progressBarBg}>
            <div className={styles.progressBarFill} style={{ width: `${winRate}%` }}></div>
          </div>
        </div>

        <div className={styles.statsGrid}>
          <div className={styles.statBox}>
            <span className={styles.statLabel}>Main</span>
            <span className={styles.statVal}>{totalMatches}</span>
          </div>
          <div className={styles.statBox}>
            <span className={`${styles.statLabel} ${styles.winColor}`}>Menang</span>
            <span className={styles.statVal}>{stats.wins}</span>
          </div>
          <div className={styles.statBox}>
            <span className={`${styles.statLabel} ${styles.lossColor}`}>Kalah</span>
            <span className={styles.statVal}>{stats.losses}</span>
          </div>
          <div className={styles.statBox}>
            <span className={`${styles.statLabel} ${styles.drawColor}`}>Seri</span>
            <span className={styles.statVal}>{stats.draws}</span>
          </div>
        </div>

        {/* Wins Breakdown */}
        <div className={styles.breakdown}>
          <h4 className={styles.subTitle}>Kemenangan Per AI:</h4>
          <div className={styles.breakdownGrid}>
            <div className={styles.breakdownItem}>
              <span className={styles.breakdownLabel}>Reguler:</span>
              <span className={styles.breakdownVal}>{stats.regulerWins || 0}</span>
            </div>
            <div className={styles.breakdownItem}>
              <span className={styles.breakdownLabel}>Hard:</span>
              <span className={styles.breakdownVal}>{stats.hardWins || 0}</span>
            </div>
            <div className={styles.breakdownItem}>
              <span className={styles.breakdownLabel}>Hell:</span>
              <span className={styles.breakdownVal}>{stats.hellWins || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Match History Section */}
      <div className={styles.historyCard}>
        <h3 className={styles.sectionTitle}>Riwayat Pertandingan</h3>
        <div className={styles.historyList}>
          {history && history.length > 0 ? (
            history.map((match) => (
              <div key={match.id} className={styles.historyItem}>
                <div className={styles.historyMain}>
                  <span className={`${styles.outcomeBadge} ${styles[match.outcome]}`}>
                    {match.outcome === "win" ? "MENANG" : match.outcome === "loss" ? "KALAH" : "SERI"}
                  </span>
                  <span className={styles.matchDifficulty}>vs AI {match.difficulty.toUpperCase()}</span>
                </div>
                <div className={styles.historyDetails}>
                  <span className={styles.matchDate}>{formatDate(match.date)}</span>
                  <span className={`${styles.eloChange} ${match.eloChange.startsWith("+") ? styles.plus : styles.minus}`}>
                    {match.eloChange} ELO
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className={styles.emptyHistory}>
              <p>Belum ada riwayat pertandingan.</p>
              <p className={styles.emptyNote}>Mulai pertandingan baru untuk merekam progress.</p>
            </div>
          )}
        </div>
      </div>

      {/* Logout Button */}
      <button onClick={() => dispatch(logout())} className={styles.logoutButton}>
        Keluar Akun
      </button>
    </aside>
  );
}

export default Sidebar;
